import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { isConflict } from "../../http/errors";
import { SearchDocument } from "../../http/types/documents";
import { useDocumentsApi } from "../../http/useDocuments";
import { PaginationQuery } from "../../types";
import { formatDate } from "../../utils";
import SvgDownload from "../icons/Download";
import SvgEdit from "../icons/Edit";
import DownloadLink from "../ui/DownloadLink";
import DragAndDrop from "../ui/DragAndDrop";
import ErrorText from "../ui/ErrorText";
import FileImage from "../ui/FileIcon";
import FlexContent from "../ui/FlexContent";
import Icon from "../ui/Icon";
import Input from "../ui/Input";
import { Action, getDeleteAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SearchBar from "../ui/SearchBar";
import SectionCard from "../ui/SectionCard";
import Table from "../ui/Table";
import styles from "./ProjectDocuments.module.css";

interface ProjectDocumentsProps {
  isAdmin?: boolean;
  canEdit?: boolean;
  active?: boolean;
}

const splitFileName = (fileName: string): [baseName: string, extension: string] => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? [fileName.substring(0, dotIndex), fileName.substring(dotIndex)] : [fileName, ""];
};

const ProjectDocuments: FC<ProjectDocumentsProps> = ({ isAdmin, canEdit, active }: ProjectDocumentsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const documentsApi = useDocumentsApi();

  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { setActions, setTitle } = useSearchContext();
  const isActiveTab = active ?? true;
  const searchPlaceholder = i18n.translation.documents.search;
  const title = i18n.translation.documents.plural;
  const [documentToDelete, setDocumentToDelete] = useState<SearchDocument>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentToRename, setDocumentToRename] = useState<SearchDocument>();
  const [renameValue, setRenameValue] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const renameCancelledRef = useRef(false);

  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = documentsApi.useProjectDocuments(projectId!, query, aborter.current.signal, isAdmin);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(title);
    setActions(
      <SearchBar
        placeholder={searchPlaceholder}
        value={filters.state.q ?? ""}
        onChange={(term) => {
          aborter.current.abort();
          aborter.current = new AbortController();
          filters.setSearchTerm(term);
        }}
      />,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [filters.state.q, isActiveTab, searchPlaceholder, setActions, setTitle, title]);

  const startRename = (item: SearchDocument) => {
    const [baseName] = splitFileName(item.name);
    setDocumentToRename(item);
    setRenameValue(baseName);
  };

  const cancelRename = () => {
    renameCancelledRef.current = true;
    setDocumentToRename(undefined);
  };

  const submitRename = (item: SearchDocument, baseName: string) => {
    const [, extension] = splitFileName(item.name);
    const trimmedBaseName = baseName.trim();
    const newName = `${trimmedBaseName}${extension}`;
    if (!trimmedBaseName || newName === item.name) {
      setDocumentToRename(undefined);
      return;
    }

    setIsRenaming(true);
    documentsApi
      .renameProjectDocument(projectId!, item.id, newName, isAdmin)
      .then(() => setDocumentToRename(undefined))
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsRenaming(false));
  };

  const rowActions: (item: SearchDocument) => Action[] = (item: SearchDocument) => [
    {
      title: i18n.translation.common.download,
      onClick: () =>
        api
          .downloadFile(
            `/files/project_documents/${item.id}/${encodeURIComponent(item.name)}`,
            encodeURIComponent(item.name),
          )
          .catch((err) => dispatch(showErrorNotification(i18n, err))),
      icon: SvgDownload,
    },
    getDeleteAction(
      i18n,
      () => {
        (canEdit || isAdmin) && setDocumentToDelete(item);
        return !(canEdit || isAdmin);
      },
      true,
      !canEdit && !isAdmin,
    ),
  ];

  const table = (
    <Table<SearchDocument>
      filters={filters}
      isTall
      head={
        <tr>
          <th align="left" style={{ width: 1 }}></th>
          <th align="left">{i18n.translation.common.name}</th>
          <th align="left">{i18n.translation.common.uploadedBy}</th>
          <th align="left">{i18n.translation.common.uploadedAt}</th>
          <th></th>
        </tr>
      }
      items={data?.items}
      renderRow={(item) => (
        <>
          <Table.IconTd>
            <FileImage fileName={item.name} />
          </Table.IconTd>
          <td className={documentToRename?.id === item.id ? styles.editingCell : undefined}>
            {documentToRename?.id === item.id ? (
              <div className={styles.nameCell}>
                <Input
                  autoFocus
                  className={styles.renameInput}
                  value={renameValue}
                  disabled={isRenaming}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      submitRename(item, renameValue);
                    }
                    if (e.key === "Escape") {
                      cancelRename();
                    }
                  }}
                  onBlur={() => {
                    if (renameCancelledRef.current) {
                      renameCancelledRef.current = false;
                      return;
                    }
                    submitRename(item, renameValue);
                  }}
                />
                {!!splitFileName(item.name)[1] && (
                  <span className={styles.extension}>{splitFileName(item.name)[1]}</span>
                )}
              </div>
            ) : (
              <div className={styles.nameCell}>
                <DownloadLink
                  url={`/files/project_documents/${item.id}/${encodeURIComponent(item.name)}`}
                  fileName={encodeURIComponent(item.name)}
                >
                  {item.name}
                </DownloadLink>
                {(canEdit || isAdmin) && (
                  <Icon
                    glyph={SvgEdit}
                    className={styles.renameIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      startRename(item);
                    }}
                  />
                )}
              </div>
            )}
          </td>
          <td>
            {item.uploader.firstName} {item.uploader.lastName}
          </td>
          <td>{formatDate(item.uploadedAt)}</td>
        </>
      )}
      rowActions={rowActions}
      total={data?.total}
      error={!!error}
    />
  );

  const [uploadDocumentsResult, setUploadDocumentsResult] = useState<{
    errors: string[];
    conflicts: string[];
  }>();

  const checkFinished = (total: number, result: [number, string][], onDone: () => void) => {
    if (total === result.length) {
      const errors = result.filter((x) => x[0] === 500).map((x) => x[1]);
      const conflicts = result.filter((x) => x[0] === 409).map((x) => x[1]);
      (errors.length || conflicts.length) && setUploadDocumentsResult({ errors, conflicts });
      onDone();
    }
  };

  const onDrop = (files: FileList, onDone: () => void) => {
    const results: [number, string][] = [];
    for (const file of files) {
      projectId &&
        documentsApi
          .insertProjectDocument(projectId, file, false, isAdmin)
          .then(() => {
            results.push([200, file.name]);
            checkFinished(files.length, results, onDone);
          })
          .catch((err) => {
            if (isConflict(err)) {
              results.push([409, file.name]);
              checkFinished(files.length, results, onDone);
              return;
            }
            results.push([500, file.name]);
            checkFinished(files.length, results, onDone);
          });
    }
  };

  return (
    <FlexContent padding noPaddingTop>
      <SectionCard>
        <DragAndDrop element={table} onDrop={onDrop} />
        {!!documentToDelete && (
          <ModalDialog
            onClose={() => setDocumentToDelete(undefined)}
            title={i18n.translation.documents.delete}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                onClick: () => setDocumentToDelete(undefined),
                type: "secondary",
              },
              {
                title: i18n.translation.common.yes,
                onClick: () => {
                  setIsSubmitting(true);
                  documentsApi
                    .deleteProjectDocument(projectId!, documentToDelete.id, isAdmin)
                    .then(() => setDocumentToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.documents.questions.delete}
          </ModalDialog>
        )}
        {!!uploadDocumentsResult && (
          <ModalDialog
            onClose={() => setUploadDocumentsResult(undefined)}
            title={i18n.translation.common.error}
            isSubmitting={false}
            actions={[
              {
                title: i18n.translation.common.close,
                onClick: () => setUploadDocumentsResult(undefined),
              },
            ]}
          >
            <ErrorText text={i18n.translation.documents.errors.someCouldNotBeSaved} />
            {!!uploadDocumentsResult?.conflicts.length && (
              <>
                <p>{i18n.translation.common.alreadyPresent}:</p>
                <ul>
                  {uploadDocumentsResult?.conflicts.map((x) => (
                    <li>{x}</li>
                  ))}
                </ul>
              </>
            )}
            {!!uploadDocumentsResult?.errors.length && (
              <>
                <p>{i18n.translation.common.failed}:</p>
                <ul>
                  {uploadDocumentsResult?.errors.map((x) => (
                    <li>{x}</li>
                  ))}
                </ul>
              </>
            )}
          </ModalDialog>
        )}
      </SectionCard>
    </FlexContent>
  );
};

export default ProjectDocuments;
